import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue
} from 'react-native-reanimated'

import NavigationBar from '@/components/NavigationBar'

type Config = {
  children: ReactNode
  onClose: () => void
  title: string
}

interface ContextValue {
  open: (config: Config) => void
  close: () => void
}

const PaymentDetailScreenContext = createContext<ContextValue>({
  open: () => undefined,
  close: () => undefined
})

export function usePaymentDetailScreen() {
  return useContext(PaymentDetailScreenContext)
}

interface PaymentDetailScreenProviderProps {
  children: ReactNode
}

export function PaymentDetailScreenProvider({ children }: PaymentDetailScreenProviderProps) {
  const { width: deviceWidth } = useWindowDimensions()
  const [config, setConfig] = useState<Config | null>(null)

  const offset = useSharedValue(deviceWidth)
  const open = useCallback((config: Config) => setConfig(config), [])
  const close = useCallback(() => {
    offset.value = withTiming(deviceWidth, { duration: 300 }, () => {
      runOnJS(setConfig)(null)
    })
  }, [])

  return (
    <PaymentDetailScreenContext.Provider value={{ open, close }}>
      {children}
      {config && <PaymentDetailScreen {...config} offset={offset} />}
    </PaymentDetailScreenContext.Provider>
  )
}

function PaymentDetailScreen({ children, onClose, offset, title }: Config & { offset: SharedValue<number> }) {
  const viewStyle = useAnimatedStyle(() => {
    return { transform: [{ translateX: offset.value }] }
  })

  useEffect(() => {
    offset.value = withTiming(0, { duration: 500 })
  }, [])

  // NOTE: `className` is not mapped to styles on Reanimated's animated
  // components, so overlay positioning is expressed with `style` here.
  return (
    <Animated.View style={[styles.overlay, viewStyle]}>
      <NavigationBar handleAction={onClose} hasBottomBorder={true}>
        {title}
      </NavigationBar>
      <View className={'flex-1 bg-background-secondary'}>{children}</View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    width: '100%',
    height: '100%'
  }
})
